// src/pages/auth/Register.jsx
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import useAuthStore from '@/store/authStore'
import toast from 'react-hot-toast'
import { GraduationCap, Eye, EyeOff, CheckCircle2, ArrowRight } from 'lucide-react'

const INDIAN_STATES = [
  'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa',
  'Gujarat','Haryana','Himachal Pradesh','Jharkhand','Karnataka','Kerala',
  'Madhya Pradesh','Maharashtra','Manipur','Meghalaya','Mizoram','Nagaland',
  'Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana','Tripura',
  'Uttar Pradesh','Uttarakhand','West Bengal',
  'Delhi','Puducherry','Chandigarh','Andaman and Nicobar Islands',
]

export default function Register() {
  const [showPassword, setShowPassword] = useState(false)
  const [step, setStep] = useState(1) // 1: school info, 2: admin info
  const { register: registerSchool, loading } = useAuthStore()
  const navigate = useNavigate()

  const { register, handleSubmit, watch, trigger, formState: { errors } } = useForm({
    defaultValues: { state: 'Tamil Nadu' }
  })

  const handleNextStep = async () => {
    const valid = await trigger(['school_name', 'city', 'state'])
    if (valid) setStep(2)
  }

  const onSubmit = async (data) => {
    const result = await registerSchool(data)
    if (result.success) {
      toast.success('School registered! Your 30-day free trial has started.')
      navigate('/admin/dashboard')
    } else {
      toast.error(result.error || 'Registration failed. Please try again.')
    }
  }

  const perks = [
    'Free 30-day trial — no credit card required',
    'School website live in under 30 minutes',
    'Online admission forms & fee collection',
    'Parent & teacher portals included',
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0d2757] to-[#1a3a6b] flex items-center justify-center p-4">
      <div className="w-full max-w-4xl grid grid-cols-1 md:grid-cols-2 gap-8 items-center">

        {/* Left — value prop */}
        <div className="text-white hidden md:block">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-orange-400 rounded-2xl flex items-center justify-center shadow-xl">
              <GraduationCap size={26} className="text-[#0d2757]" />
            </div>
            <span className="text-2xl font-bold">educ<span className="text-orange-400">za</span></span>
          </div>
          <h1 className="text-4xl font-bold leading-tight mb-4">
            Get your school<br />online in <span className="text-orange-400">30 minutes</span>
          </h1>
          <p className="text-white/60 mb-8 text-lg">Join schools across Tamil Nadu already using Educza.</p>
          <div className="space-y-3">
            {perks.map((p) => (
              <div key={p} className="flex items-center gap-3 text-white/80 text-sm">
                <CheckCircle2 size={18} className="text-green-400 flex-shrink-0" />
                {p}
              </div>
            ))}
          </div>
          <div className="mt-8 p-4 bg-white/10 rounded-xl border border-white/20 backdrop-blur-sm">
            <p className="text-sm text-white/60 italic">
              "Educza transformed how we manage our school. Website, fees, admissions — all in one place."
            </p>
            <p className="text-xs text-orange-300 mt-2 font-medium">— Principal, Sunshine Matric School, Chennai</p>
          </div>
        </div>

        {/* Right — form */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo mobile */}
          <div className="flex items-center gap-2 mb-6 md:hidden">
            <div className="w-9 h-9 bg-orange-400 rounded-xl flex items-center justify-center">
              <GraduationCap size={20} className="text-[#0d2757]" />
            </div>
            <span className="text-xl font-bold text-[#0d2757]">educ<span className="text-orange-400">za</span></span>
          </div>

          {/* Step indicator */}
          <div className="flex items-center gap-2 mb-6">
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${step >= 1 ? 'bg-[#0d2757] text-white' : 'bg-gray-100 text-gray-400'}`}>1</div>
            <div className={`flex-1 h-0.5 ${step >= 2 ? 'bg-[#0d2757]' : 'bg-gray-200'}`} />
            <div className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-bold ${step >= 2 ? 'bg-[#0d2757] text-white' : 'bg-gray-100 text-gray-400'}`}>2</div>
            <div className="text-xs text-gray-400 ml-1">{step === 1 ? 'School Info' : 'Your Details'}</div>
          </div>

          <form onSubmit={handleSubmit(onSubmit)}>

            {/* ── STEP 1: School Info ── */}
            {step === 1 && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-800">Tell us about your school</h2>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">School Name <span className="text-red-500">*</span></label>
                  <input
                    {...register('school_name', { required: 'School name is required', minLength: { value: 3, message: 'Must be at least 3 characters' } })}
                    placeholder="e.g. Sunshine Matriculation School"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757]/20 focus:border-[#0d2757]"
                  />
                  {errors.school_name && <p className="text-red-500 text-xs mt-1">{errors.school_name.message}</p>}
                  <p className="text-xs text-gray-400 mt-1">Your school URL will be: <strong>{watch('school_name') ? watch('school_name').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') : 'your-school'}.educza.com</strong></p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      {...register('city')}
                      placeholder="Chennai"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757]/20 focus:border-[#0d2757]"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                    <select
                      {...register('state')}
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757]/20 focus:border-[#0d2757] bg-white"
                    >
                      {INDIAN_STATES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={handleNextStep}
                  className="w-full bg-[#0d2757] text-white rounded-lg py-3 text-sm font-semibold hover:bg-[#1a3a6b] transition-colors flex items-center justify-center gap-2"
                >
                  Continue <ArrowRight size={16} />
                </button>
              </div>
            )}

            {/* ── STEP 2: Admin Info ── */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <button type="button" onClick={() => setStep(1)} className="text-gray-400 hover:text-gray-600 text-sm">← Back</button>
                  <h2 className="text-xl font-bold text-gray-800">Create your admin account</h2>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Full Name <span className="text-red-500">*</span></label>
                  <input
                    {...register('name', { required: 'Your name is required' })}
                    placeholder="Ramesh Kumar"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757]/20 focus:border-[#0d2757]"
                  />
                  {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email Address <span className="text-red-500">*</span></label>
                  <input
                    {...register('email', {
                      required: 'Email is required',
                      pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email address' },
                    })}
                    type="email"
                    placeholder="principal@school.com"
                    className="w-full border border-gray-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757]/20 focus:border-[#0d2757]"
                  />
                  {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number <span className="text-red-500">*</span></label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 rounded-l-lg bg-gray-50 text-gray-500 text-sm">+91</span>
                    <input
                      {...register('phone', {
                        required: 'Phone is required',
                        pattern: { value: /^[6-9]\d{9}$/, message: 'Enter valid 10-digit mobile number' },
                      })}
                      type="tel" maxLength={10}
                      placeholder="9876543210"
                      className="flex-1 border border-gray-300 rounded-r-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757]/20 focus:border-[#0d2757]"
                    />
                  </div>
                  {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Password <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      {...register('password', {
                        required: 'Password is required',
                        minLength: { value: 8, message: 'Minimum 8 characters' },
                      })}
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Min. 8 characters"
                      className="w-full border border-gray-300 rounded-lg px-4 py-2.5 pr-10 text-sm focus:outline-none focus:ring-2 focus:ring-[#0d2757]/20 focus:border-[#0d2757]"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password.message}</p>}
                </div>

                <p className="text-xs text-gray-400 bg-gray-50 rounded-lg p-3 leading-relaxed">
                  By registering, you agree to Educza's Terms of Service and Privacy Policy.
                  Your 30-day free trial starts immediately — no credit card required.
                </p>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-500 text-white rounded-lg py-3 text-sm font-semibold hover:bg-orange-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Registering...</>
                  ) : (
                    <><GraduationCap size={16} /> Start Free Trial</>
                  )}
                </button>
              </div>
            )}
          </form>

          <p className="text-center text-sm text-gray-500 mt-5">
            Already have an account?{' '}
            <Link to="/login" className="text-[#0d2757] font-semibold hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  )
}
